import React, { useState, useEffect } from 'react';
import './CommissionCalculator.css';

const CommissionCalculator = () => {
  const [calculatorType, setCalculatorType] = useState('security'); // 'security' or 'printing'
  const [currentCycle, setCurrentCycle] = useState(1); // Current 30-day cycle
  const [dailySales, setDailySales] = useState({}); // Daily sales for current cycle
  const [cumulativeSales, setCumulativeSales] = useState(0); // Total sales for current cycle
  const [costOfSale, setCostOfSale] = useState(500); // Total cost of sale for cycle
  const [showAdmin, setShowAdmin] = useState(false); // Show/hide admin section
  const [isLoggedIn, setIsLoggedIn] = useState(false); // Admin login status
  const [showLogin, setShowLogin] = useState(false); // Show/hide login form
  const [loginCredentials, setLoginCredentials] = useState({ username: '', password: '' }); // Login form data
  const [salesPeople, setSalesPeople] = useState([
    { id: 1, name: 'Justice', department: 'security', active: true },
    { id: 2, name: 'Bongile', department: 'security', active: true },
    { id: 3, name: 'Dube', department: 'security', active: true },
    { id: 4, name: 'Mmapule', department: 'printing', active: true },
    { id: 5, name: 'Justice', department: 'printing', active: true },
    { id: 6, name: 'Widson', department: 'printing', active: true }
  ]); // Sales people list
  const [editingSalesPerson, setEditingSalesPerson] = useState(null); // Currently editing sales person
  const [showSalesPersonModal, setShowSalesPersonModal] = useState(false); // Show/hide sales person modal
  const [results, setResults] = useState({
    commissionAmount: 0,
    netCommission: 0,
    breakdown: [],
    targetProgress: []
  });
  
  // New state for historical data
  const [historicalData, setHistoricalData] = useState({}); // Store monthly performance data
  const [showHistoricalView, setShowHistoricalView] = useState(false); // Toggle historical view
  const [selectedMonth, setSelectedMonth] = useState(''); // Selected month for historical view

  // Admin credentials (in a real app, this would be server-side)
  const ADMIN_CREDENTIALS = {
    username: 'admin',
    password: 'ingwenya2024'
  };

  // Commission structures for different departments
  const commissionStructures = {
    security: [
      { tier: 'A', range: 'R0 - R30K', rate: '3%', maxAmount: 30000, rateValue: 0.03, achieved: false },
      { tier: 'B', range: 'R30K - R60K', rate: '5%', maxAmount: 60000, rateValue: 0.05, achieved: false },
      { tier: 'C', range: 'R60K - R90K', rate: '7.5%', maxAmount: 90000, rateValue: 0.075, achieved: false },
      { tier: 'D', range: 'R90K+', rate: '7.5%', maxAmount: Infinity, rateValue: 0.075, achieved: false }
    ],
    printing: [
      { tier: 'A', range: 'R70K - R90K', rate: '3%', maxAmount: 90000, rateValue: 0.03, achieved: false },
      { tier: 'B', range: 'R90K - R120K', rate: '5%', maxAmount: 120000, rateValue: 0.05, achieved: false },
      { tier: 'C', range: 'R120K - R150K', rate: '7.5%', maxAmount: 150000, rateValue: 0.075, achieved: false },
      { tier: 'D', range: 'R150K+', rate: '10%', maxAmount: Infinity, rateValue: 0.10, achieved: false }
    ]
  };

  // Initialize daily sales for current cycle
  useEffect(() => {
    const days = {};
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = currentDate.getDate();
    
    let workingDayCount = 0;
    
    // Generate all working days for the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      
      // Only include working days (Monday = 1, Tuesday = 2, Wednesday = 3, Thursday = 4, Friday = 5)
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        workingDayCount++;
        const dateString = date.toLocaleDateString('en-ZA', { 
          day: '2-digit', 
          month: 'short',
          weekday: 'short'
        });
        
        const isPastDate = day < today;
        const isToday = day === today;
        
        days[workingDayCount] = { 
          amount: 0, 
          date: dateString,
          fullDate: date.toISOString().split('T')[0],
          actualDay: day,
          dayOfWeek: dayOfWeek,
          salesPersonId: null, // Link to sales person
          isPast: isPastDate,
          isToday: isToday,
          sortOrder: isPastDate ? 1 : 0 // 0 for future/current, 1 for past (to move past dates to bottom)
        };
      }
    }
    setDailySales(days);
    
    // Initialize results with no sales
    const currentStructure = commissionStructures[calculatorType];
    setResults({
      commissionAmount: 0,
      netCommission: 0,
      breakdown: [],
      targetProgress: currentStructure.map(target => ({
        ...target,
        achieved: false,
        progress: 0,
        remaining: target.maxAmount
      }))
    });
  }, [currentCycle, calculatorType]);

  // Initialize results when component first mounts
  useEffect(() => {
    const currentStructure = commissionStructures[calculatorType];
    setResults(prev => ({
      ...prev,
      targetProgress: currentStructure.map(target => ({
        ...target,
        achieved: false,
        progress: 0,
        remaining: target.maxAmount
      }))
    }));
  }, [calculatorType]);

  // Set initial results immediately when component mounts
  useEffect(() => {
    const currentStructure = commissionStructures[calculatorType];
    setResults({
      commissionAmount: 0,
      netCommission: 0,
      breakdown: [],
      targetProgress: currentStructure.map(target => ({
        ...target,
        achieved: false,
        progress: 0,
        remaining: target.maxAmount
      }))
    });
  }, []); // Empty dependency array means this runs once when component mounts

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'costOfSale') {
      setCostOfSale(parseFloat(value) || 0);
    }
  };

  const handleCalculatorTypeChange = (type) => {
    setCalculatorType(type);
    // Reset results when switching calculator types
    const currentStructure = commissionStructures[type];
    setResults({
      commissionAmount: 0,
      netCommission: 0,
      breakdown: [],
      targetProgress: currentStructure.map(target => ({
        ...target,
        achieved: false,
        progress: 0,
        remaining: target.maxAmount
      }))
    });
  };

  const handleDailySaleChange = (day, value) => {
    const newDailySales = { ...dailySales };
    newDailySales[day] = { 
      ...newDailySales[day], 
      amount: parseFloat(value) || 0 
    };
    setDailySales(newDailySales);
    
    // Calculate cumulative sales
    const total = Object.values(newDailySales).reduce((sum, sale) => sum + sale.amount, 0);
    setCumulativeSales(total);
    
    // Auto-calculate commission when sales change
    calculateCommission(total, costOfSale);
  };

  const handleSalesPersonChange = (day, salesPersonId) => {
    const newDailySales = { ...dailySales };
    newDailySales[day] = { 
      ...newDailySales[day], 
      salesPersonId: salesPersonId === '' ? null : parseInt(salesPersonId)
    };
    setDailySales(newDailySales);
  };

  // Sales Person Management Functions
  const addSalesPerson = () => {
    setEditingSalesPerson({
      id: Date.now(),
      name: '',
      department: calculatorType,
      active: true
    });
    setShowSalesPersonModal(true);
  };

  const editSalesPerson = (salesPerson) => {
    setEditingSalesPerson({ ...salesPerson });
    setShowSalesPersonModal(true);
  };

  const deleteSalesPerson = (id) => {
    if (window.confirm('Are you sure you want to delete this sales person?')) {
      setSalesPeople(prev => prev.filter(sp => sp.id !== id));
      // Remove sales person from daily sales
      const newDailySales = { ...dailySales };
      Object.keys(newDailySales).forEach(day => {
        if (newDailySales[day].salesPersonId === id) {
          newDailySales[day].salesPersonId = null;
        }
      });
      setDailySales(newDailySales);
    }
  };

  const saveSalesPerson = () => {
    if (!editingSalesPerson.name.trim()) {
      alert('Please enter a name for the sales person');
      return;
    }

    if (editingSalesPerson.id) {
      // Update existing
      setSalesPeople(prev => 
        prev.map(sp => 
          sp.id === editingSalesPerson.id ? editingSalesPerson : sp
        )
      );
    } else {
      // Add new
      setSalesPeople(prev => [...prev, { ...editingSalesPerson, id: Date.now() }]);
    }

    setShowSalesPersonModal(false);
    setEditingSalesPerson(null);
  };

  const cancelSalesPersonEdit = () => {
    setShowSalesPersonModal(false);
    setEditingSalesPerson(null);
  };

  const getSalesPersonName = (id) => {
    const salesPerson = salesPeople.find(sp => sp.id === id);
    return salesPerson ? salesPerson.name : 'Unassigned';
  };

  const getSalesPersonStats = () => {
    const stats = {};
    salesPeople.forEach(sp => {
      if (sp.active && sp.department === calculatorType) {
        const totalSales = Object.values(dailySales).reduce((sum, sale) => {
          return sale.salesPersonId === sp.id ? sum + sale.amount : sum;
        }, 0);
        
        // Calculate commission for this sales person
        let commissionAmount = 0;
        if (totalSales > 0) {
          const currentStructure = commissionStructures[calculatorType];
          
          // Check if sales meet minimum threshold for printing department
          if (calculatorType === 'printing' && totalSales < 70000) {
            commissionAmount = 0;
          } else {
            // Calculate commission based on current structure
            let previousMax = calculatorType === 'printing' ? 70000 : 0;
            
            currentStructure.forEach((target) => {
              if (totalSales > previousMax) {
                const targetAmount = Math.min(totalSales - previousMax, target.maxAmount - previousMax);
                if (targetAmount > 0) {
                  const targetCommission = targetAmount * target.rateValue;
                  commissionAmount += targetCommission;
                }
                previousMax = target.maxAmount;
              }
            });
          }
        }
        
        stats[sp.id] = {
          name: sp.name,
          totalSales,
          commission: commissionAmount
        };
      }
    });
    
    // Sort by total sales (highest first) and return sorted array
    return Object.entries(stats)
      .sort(([, a], [, b]) => b.totalSales - a.totalSales)
      .map(([id, data]) => ({ id, ...data }));
  };

  const calculateCommission = (totalSales, totalCost) => {
    const currentStructure = commissionStructures[calculatorType];
    
    console.log('Calculating with:', { totalSales, totalCost, department: calculatorType });

    let commissionAmount = 0;
    let commissionBreakdown = [];
    let targetProgress = [];
    
    // If no sales, show all targets as not achieved
    if (totalSales <= 0) {
      setResults({
        commissionAmount: 'R0.00',
        netCommission: 'R0.00',
        breakdown: [],
        targetProgress: currentStructure.map(target => ({
          ...target,
          achieved: false,
          progress: 0,
          remaining: target.maxAmount
        }))
      });
      return;
    }
    
    // Check if sales meet minimum threshold for printing department
    if (calculatorType === 'printing' && totalSales < 70000) {
      console.log('Sales below minimum threshold for printing department (R70,000)');
      setResults({
        commissionAmount: 'R0.00',
        netCommission: 'R0.00',
        breakdown: [],
        targetProgress: currentStructure.map(target => ({
          ...target,
          achieved: false,
          progress: Math.min((totalSales / target.maxAmount) * 100, 100),
          remaining: Math.max(target.maxAmount - totalSales, 0)
        }))
      });
      return;
    }
    
    // Calculate commission based on current structure
    let previousMax = calculatorType === 'printing' ? 70000 : 0;
    
    currentStructure.forEach((target, index) => {
      const achieved = totalSales >= target.maxAmount;
      const progress = Math.min((totalSales / target.maxAmount) * 100, 100);
      const remaining = Math.max(target.maxAmount - totalSales, 0);
      
      targetProgress.push({
        ...target,
        achieved,
        progress,
        remaining
      });

      if (totalSales > previousMax) {
        const targetAmount = Math.min(totalSales - previousMax, target.maxAmount - previousMax);
        if (targetAmount > 0) {
          const targetCommission = targetAmount * target.rateValue;
          commissionAmount += targetCommission;
          commissionBreakdown.push({
            tier: target.tier,
            range: target.range,
            rate: target.rate,
            amount: targetAmount,
            commission: targetCommission
          });
          console.log(`${target.tier} (${target.rate}):`, targetAmount, '=', targetCommission);
        }
        previousMax = target.maxAmount;
      }
    });

    const netCommission = Math.max(0, commissionAmount - totalCost);
    console.log('Net commission after deduction:', netCommission);

    const formatZAR = (amount) => {
      return new Intl.NumberFormat('en-ZA', {
        style: 'currency',
        currency: 'ZAR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    };

    const formattedCommission = formatZAR(commissionAmount);
    const formattedNetCommission = formatZAR(netCommission);

    console.log('Formatted results:', { formattedCommission, formattedNetCommission });

    setResults({
      commissionAmount: formattedCommission,
      netCommission: formattedNetCommission,
      breakdown: commissionBreakdown,
      targetProgress
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    calculateCommission(cumulativeSales, costOfSale);
  };

  const resetCycle = () => {
    setCurrentCycle(prev => prev + 1);
    setCumulativeSales(0);
    setCostOfSale(500);
    setResults({
      commissionAmount: 0,
      netCommission: 0,
      breakdown: [],
      targetProgress: []
    });
  };

  const formatZAR = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Historical data functions
  const saveMonthlyPerformance = () => {
    const currentDate = new Date();
    const monthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
    const monthDisplay = currentDate.toLocaleDateString('en-ZA', { 
      month: 'long', 
      year: 'numeric' 
    });
    
    // Get current performance data for all sales people
    const currentPerformance = getSalesPersonStats();
    
    // Create monthly record
    const monthlyRecord = {
      month: monthKey,
      monthDisplay: monthDisplay,
      department: calculatorType,
      dateCreated: new Date().toISOString(),
      totalSales: cumulativeSales,
      costOfSale: costOfSale,
      commissionAmount: results.commissionAmount,
      netCommission: results.netCommission,
      dailySales: dailySales,
      salesPeoplePerformance: currentPerformance,
      commissionStructure: commissionStructures[calculatorType]
    };
    
    // Save to historical data
    setHistoricalData(prev => ({
      ...prev,
      [monthKey]: monthlyRecord
    }));
    
    // Save to localStorage for persistence
    localStorage.setItem('commissionCalculatorHistoricalData', JSON.stringify({
      ...historicalData,
      [monthKey]: monthlyRecord
    }));
    
    alert(`Monthly performance data for ${monthDisplay} has been saved successfully!`);
  };

  const loadHistoricalData = () => {
    const saved = localStorage.getItem('commissionCalculatorHistoricalData');
    if (saved) {
      setHistoricalData(JSON.parse(saved));
    }
  };

  const getHistoricalMonths = () => {
    return Object.keys(historicalData)
      .filter(key => historicalData[key].department === calculatorType)
      .sort((a, b) => b.localeCompare(a)); // Most recent first
  };

  const getHistoricalPerformance = (monthKey) => {
    return historicalData[monthKey];
  };

  const exportHistoricalData = (monthKey) => {
    const data = historicalData[monthKey];
    if (!data) return;
    
    const csvContent = generateCSV(data);
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `commission-data-${data.monthDisplay.replace(/\s+/g, '-')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Admin login functions
  const handleLogin = (e) => {
    e.preventDefault();
    if (loginCredentials.username === ADMIN_CREDENTIALS.username && 
        loginCredentials.password === ADMIN_CREDENTIALS.password) {
      setIsLoggedIn(true);
      setShowLogin(false);
      setLoginCredentials({ username: '', password: '' });
      setShowAdmin(true);
    } else {
      alert('Invalid credentials. Please try again.');
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setShowAdmin(false);
    setLoginCredentials({ username: '', password: '' });
  };

  const openLoginForm = () => {
    setShowLogin(true);
    setLoginCredentials({ username: '', password: '' });
  };

  const generateCSV = (data) => {
    let csv = 'Month,Department,Total Sales,Commission Amount,Net Commission,Cost of Sale\n';
    csv += `${data.monthDisplay},${data.department},${data.totalSales},${data.commissionAmount},${data.netCommission},${data.costOfSale}\n\n`;
    
    csv += 'Sales Person Performance\n';
    csv += 'Name,Total Sales,Commission\n';
    data.salesPeoplePerformance.forEach(person => {
      csv += `${person.name},${person.totalSales},${person.commission}\n`;
    });
    
    csv += '\nDaily Sales Breakdown\n';
    csv += 'Date,Amount,Sales Person\n';
    Object.entries(data.dailySales).forEach(([day, sale]) => {
      const salesPersonName = sale.salesPersonId ? 
        salesPeople.find(sp => sp.id === sale.salesPersonId)?.name || 'Unknown' : 
        'Unassigned';
      csv += `${sale.date},${sale.amount},${salesPersonName}\n`;
    });
    
    return csv;
  };

  // Load historical data on component mount
  useEffect(() => {
    loadHistoricalData();
  }, []);

  // Calculate initial values when component loads or calculator type changes
  useEffect(() => {
    // Only calculate commission if there are actual sales
    if (cumulativeSales > 0) {
      calculateCommission(cumulativeSales, costOfSale);
    } else {
      // Reset results when no sales
      setResults({
        commissionAmount: 0,
        netCommission: 0,
        breakdown: [],
        targetProgress: []
      });
    }
  }, [calculatorType]);

  // Get current month name and year
  const getCurrentMonthDisplay = () => {
    const currentDate = new Date();
    return currentDate.toLocaleDateString('en-ZA', { 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Get remaining working days count from today
  const getWorkingDaysCount = () => {
    return Object.keys(dailySales).length;
  };

  return (
    <div className="commission-calculator">
      {/* Month and Year Display - Outside the main container */}
      <div className="month-year-display">
        <h1>{getCurrentMonthDisplay()}</h1>
      </div>
      
      <div className={`calculator-container ${calculatorType === 'printing' ? 'printing' : ''}`}>
        <h2>Sales Tracking & Commission Calculator</h2>

        {/* Calculator Type Toggle */}
        <div className="calculator-type-toggle">
          <label>Select Department:</label>
          <div className="toggle-buttons">
            <button
              type="button"
              className={`toggle-btn ${calculatorType === 'security' ? 'active' : ''}`}
              onClick={() => handleCalculatorTypeChange('security')}
            >
              Security Department
            </button>
            <button
              type="button"
              className={`toggle-btn ${calculatorType === 'printing' ? 'active printing' : ''}`}
              onClick={() => handleCalculatorTypeChange('printing')}
            >
              Printing Department
            </button>
          </div>
        </div>

        {/* Cycle Information */}
        <div className="cycle-info">
          <div className="cycle-stats">
            <div className="stat-item">
              <span className="stat-label">Total Sales:</span>
              <span className="stat-value">{formatZAR(cumulativeSales)}</span>
            </div>
            
            <div className="stat-item">
              <span className="stat-label">Total Working Days:</span>
              <span className="stat-value">{Object.keys(dailySales).length}</span>
            </div>
            
            <div className="stat-item">
              <span className="stat-label">Days Remaining:</span>
              <span className="stat-value">{Object.values(dailySales).filter(sale => !sale.isPast && sale.amount === 0).length}</span>
            </div>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="calculator-form">
          <div className="form-group">
            <label>Commission Structure - {calculatorType === 'security' ? 'Security' : 'Printing'} Department</label>
            <div className="commission-structure">
              {commissionStructures[calculatorType].map((target, index) => (
                <div key={index} className="structure-item">
                  <span className="tier">{target.tier}:</span>
                  <span className="range">{target.range}</span>
                  <span className="rate">{target.rate}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Sales Performance Summary */}
          <div className="sales-performance-summary">
            <h3>Sales Performance Summary</h3>
            <div className="performance-grid">
              {getSalesPersonStats().length > 0 ? (
                getSalesPersonStats().map((stats, index) => (
                  <div key={stats.id} className="performance-item">
                    <div className="performance-header">
                      <span className="rank-badge">#{index + 1}</span>
                      <span className="sales-person-name">{stats.name}</span>
                    </div>
                    <div className="performance-stats">
                      <div className="stat">
                        <span className="stat-label">Total Sales:</span>
                        <span className="stat-value">{formatZAR(stats.totalSales)}</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Commission:</span>
                        <span className="stat-value">{formatZAR(stats.commission)}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="performance-item no-data">
                  <div className="performance-header">
                    <span className="no-data-message">No sales data available</span>
                  </div>
                  <div className="performance-stats">
                    <div className="stat">
                      <span className="stat-label">Start entering daily sales to see rankings</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Daily Sales Input (R) - All Working Days (Past dates are closed)</label>
            <div className="daily-sales-grid">
              {Object.entries(dailySales)
                .sort(([, a], [, b]) => {
                  // Sort by sortOrder first (past dates go to bottom), then by actual day
                  if (a.sortOrder !== b.sortOrder) {
                    return a.sortOrder - b.sortOrder;
                  }
                  return a.actualDay - b.actualDay;
                })
                .map(([workingDay, sale]) => (
                  <div key={workingDay} className={`daily-sale-input ${sale.isPast ? 'past-date' : ''} ${sale.isToday ? 'today' : ''}`}>
                    <label htmlFor={`day${workingDay}`}>
                      {sale.date}
                      
                      {sale.isPast && <span className="past-indicator"> (Closed)</span>}
                    </label>
                    <div className="sales-input-row">
                      <input
                        type="number"
                        id={`day${workingDay}`}
                        value={sale.amount || ''}
                        onChange={(e) => handleDailySaleChange(workingDay, e.target.value)}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        disabled={sale.isPast}
                      />
                      <select
                        value={sale.salesPersonId || ''}
                        onChange={(e) => handleSalesPersonChange(workingDay, e.target.value)}
                        className="sales-person-select"
                        disabled={sale.isPast}
                      >
                        <option value="">Unassigned</option>
                        {salesPeople
                          .filter(sp => sp.department === calculatorType && sp.active)
                          .map(sp => (
                            <option key={sp.id} value={sp.id}>
                              {sp.name}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="costOfSale">Total Cost of Sale Deduction for Cycle (R)</label>
            <input
              type="number"
              id="costOfSale"
              name="costOfSale"
              value={costOfSale}
              onChange={handleInputChange}
              placeholder="Enter total cost of sale deduction for cycle"
              step="0.01"
              min="0"
            />
          </div>

          <div className="button-group">
            <button type="submit" className="btn-calculate">
              Calculate Commission
            </button>
            <button type="button" onClick={resetCycle} className="btn-reset">
              Start New Cycle
            </button>
          </div>
        </form>

        {/* Target Progress Display */}
        {results.targetProgress && results.targetProgress.length > 0 && (
          <div className="target-progress-section">
            <h3>Target Progress</h3>
            <div className="target-progress-grid">
              {results.targetProgress.map((target, index) => (
                <div key={index} className={`target-progress-item ${target.achieved ? 'achieved' : ''}`}>
                  <div className="target-header">
                    <span className="target-name">{target.tier}</span>
                    <span className="target-rate">{target.rate}</span>
                  </div>
                  <div className="target-range">{target.range}</div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${target.progress}%` }}
                    ></div>
                  </div>
                  <div className="target-status">
                    {target.achieved ? (
                      <span className="status-achieved">✓ Target Achieved!</span>
                    ) : (
                      <span className="status-remaining">
                        {formatZAR(target.remaining)} remaining
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {results.commissionAmount !== 0 && (
          <div className="results-section">
            <h3>Commission Results - {calculatorType === 'security' ? 'Security' : 'Printing'} Department</h3>
            
            {/* Commission Breakdown */}
            <div className="breakdown-section">
              <h4>Commission Breakdown by Target</h4>
              {results.breakdown && results.breakdown.map((tier, index) => (
                <div key={index} className="breakdown-item">
                  <div className="breakdown-header">
                    <span className="tier-name">{tier.tier}</span>
                    <span className="tier-range">{tier.range}</span>
                    <span className="tier-rate">{tier.rate}</span>
                  </div>
                  <div className="breakdown-details">
                    <span>Amount: {formatZAR(tier.amount)}</span>
                    <span>Commission: {formatZAR(tier.commission)}</span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="result-item">
              <span className="result-label">Total Commission Amount:</span>
              <span className="result-value">{results.commissionAmount}</span>
            </div>
            <div className="result-item">
              <span className="result-label">Total Cost of Sale Deduction:</span>
              <span className="result-value">{formatZAR(costOfSale)}</span>
            </div>
            <div className="result-item total">
              <span className="result-label">Net Commission:</span>
              <span className="result-value">{results.netCommission}</span>
            </div>
          </div>
        )}

        {/* Admin Access - Hidden by default */}
        <div className="admin-access">
          {!isLoggedIn ? (
            <button 
              type="button"
              onClick={openLoginForm}
              className="btn-admin-login"
            >
              Admin Access
            </button>
          ) : (
            <div className="admin-controls">
              <button 
                type="button"
                onClick={() => setShowAdmin(!showAdmin)}
                className="btn-admin-toggle"
              >
                {showAdmin ? 'Hide Admin Panel' : 'Show Admin Panel'}
              </button>
              <button 
                type="button"
                onClick={handleLogout}
                className="btn-admin-logout"
              >
                Logout
              </button>
            </div>
          )}
        </div>

        {/* Sales People Management (Admin) */}
        {showAdmin && (
          <div className="admin-section">
            <h3>Sales People Management (Admin)</h3>
            
            {/* Sales People List */}
            <div className="sales-people-list">
              <h4>Current Sales Team</h4>
              <div className="sales-people-grid">
                {salesPeople
                  .filter(sp => sp.department === calculatorType && sp.active)
                  .map(salesPerson => (
                    <div key={salesPerson.id} className="sales-person-card">
                      <div className="sales-person-info">
                        <span className="sales-person-name">{salesPerson.name}</span>
                        <span className="status-badge active">
                          ✓
                        </span>
                      </div>
                      <div className="sales-person-actions">
                        <button 
                          onClick={() => editSalesPerson(salesPerson)}
                          className="btn-edit"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button 
                          onClick={() => deleteSalesPerson(salesPerson.id)}
                          className="btn-delete"
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
              <button onClick={addSalesPerson} className="btn-add" title="Add New Sales Person">
                ➕
              </button>
            </div>

            {/* Performance Summary */}
            <div className="performance-summary">
              <h4>Performance Summary</h4>
              <div className="performance-grid">
                {getSalesPersonStats().length > 0 ? (
                  getSalesPersonStats().map((stats, index) => (
                    <div key={stats.id} className="performance-item">
                      <div className="performance-header">
                        <span className="rank-badge">#{index + 1}</span>
                        <span className="sales-person-name">{stats.name}</span>
                      </div>
                      <div className="performance-stats">
                        <div className="stat">
                          <span className="stat-label">Total Sales:</span>
                          <span className="stat-value">{formatZAR(stats.totalSales)}</span>
                        </div>
                        <div className="stat">
                          <span className="stat-label">Commission:</span>
                          <span className="stat-value">{formatZAR(stats.commission)}</span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="performance-item no-data">
                    <div className="performance-header">
                      <span className="no-data-message">No sales data available</span>
                    </div>
                    <div className="performance-stats">
                      <div className="stat">
                        <span className="stat-label">Start entering daily sales to see rankings</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Historical Data Management */}
            <div className="historical-data-section">
              <h4>Historical Data Management</h4>
              
              {/* Save Current Month Button */}
              <div className="save-month-section">
                <button 
                  onClick={saveMonthlyPerformance}
                  className="btn-save-month"
                  disabled={cumulativeSales === 0}
                >
                  Save Current Month's Performance
                </button>
                <span className="save-note">
                  Save this month's data for commission payment and dispute resolution
                </span>
              </div>

              {/* Historical Data Toggle */}
              <div className="historical-toggle">
                <button 
                  type="button"
                  onClick={() => setShowHistoricalView(!showHistoricalView)}
                  className="btn-toggle-historical"
                >
                  {showHistoricalView ? 'Hide Historical Data' : 'View Historical Data'}
                </button>
              </div>

              {/* Historical Data View */}
              {showHistoricalView && (
                <div className="historical-data-view">
                  <div className="historical-controls">
                    <select 
                      value={selectedMonth} 
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      className="month-selector"
                    >
                      <option value="">Select Month</option>
                      {getHistoricalMonths().map(monthKey => {
                        const data = historicalData[monthKey];
                        return (
                          <option key={monthKey} value={monthKey}>
                            {data.monthDisplay} - {data.department}
                          </option>
                        );
                      })}
                    </select>
                    
                    {selectedMonth && (
                      <button 
                        onClick={() => exportHistoricalData(selectedMonth)}
                        className="btn-export"
                      >
                        Export to CSV
                      </button>
                    )}
                  </div>

                  {/* Selected Month Data Display */}
                  {selectedMonth && historicalData[selectedMonth] && (
                    <div className="selected-month-data">
                      <h5>{historicalData[selectedMonth].monthDisplay} Performance</h5>
                      
                      <div className="month-summary">
                        <div className="summary-item">
                          <span className="summary-label">Total Sales:</span>
                          <span className="summary-value">{formatZAR(historicalData[selectedMonth].totalSales)}</span>
                        </div>
                        <div className="summary-item">
                          <span className="summary-label">Commission:</span>
                          <span className="summary-value">{historicalData[selectedMonth].commissionAmount}</span>
                        </div>
                        <div className="summary-item">
                          <span className="summary-label">Net Commission:</span>
                          <span className="summary-value">{historicalData[selectedMonth].netCommission}</span>
                        </div>
                        <div className="summary-item">
                          <span className="summary-label">Cost of Sale:</span>
                          <span className="summary-value">{formatZAR(historicalData[selectedMonth].costOfSale)}</span>
                        </div>
                      </div>

                      {/* Historical Sales People Performance */}
                      <div className="historical-performance">
                        <h6>Sales People Performance</h6>
                        <div className="historical-performance-grid">
                          {historicalData[selectedMonth].salesPeoplePerformance.map((person, index) => (
                            <div key={person.id} className="historical-performance-item">
                              <div className="person-header">
                                <span className="rank-badge">#{index + 1}</span>
                                <span className="person-name">{person.name}</span>
                              </div>
                              <div className="person-stats">
                                <div className="stat">
                                  <span className="stat-label">Sales:</span>
                                  <span className="stat-value">{formatZAR(person.totalSales)}</span>
                                </div>
                                <div className="stat">
                                  <span className="stat-label">Commission:</span>
                                  <span className="stat-value">{formatZAR(person.commission)}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Daily Sales Breakdown */}
                      <div className="daily-breakdown">
                        <h6>Daily Sales Breakdown</h6>
                        <div className="daily-breakdown-grid">
                          {Object.entries(historicalData[selectedMonth].dailySales)
                            .sort(([, a], [, b]) => a.actualDay - b.actualDay)
                            .map(([day, sale]) => (
                              <div key={day} className="daily-breakdown-item">
                                <span className="date">{sale.date}</span>
                                <span className="amount">{formatZAR(sale.amount)}</span>
                                <span className="sales-person">
                                  {sale.salesPersonId ? 
                                    salesPeople.find(sp => sp.id === sale.salesPersonId)?.name || 'Unknown' : 
                                    'Unassigned'}
                                </span>
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* No Historical Data Message */}
                  {showHistoricalView && getHistoricalMonths().length === 0 && (
                    <div className="no-historical-data">
                      <p>No historical data available yet. Save the current month's performance to start building a history.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Admin Login Modal */}
      {showLogin && (
        <div className="modal-overlay">
          <div className="modal-content login-modal">
            <h2>Admin Login</h2>
            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label>Username:</label>
                <input
                  type="text"
                  value={loginCredentials.username}
                  onChange={(e) => setLoginCredentials({ ...loginCredentials, username: e.target.value })}
                  required
                  placeholder="Enter username"
                />
              </div>
              <div className="form-group">
                <label>Password:</label>
                <input
                  type="password"
                  value={loginCredentials.password}
                  onChange={(e) => setLoginCredentials({ ...loginCredentials, password: e.target.value })}
                  required
                  placeholder="Enter password"
                />
              </div>
              <div className="button-group">
                <button type="submit" className="btn-save">Login</button>
                <button type="button" onClick={() => setShowLogin(false)} className="btn-cancel">Cancel</button>
              </div>
            </form>
            <div className="login-note">
              <p><strong>Default credentials:</strong></p>
              <p>Username: admin</p>
              <p>Password: ingwenya2024</p>
            </div>
          </div>
        </div>
      )}

      {/* Sales Person Modal */}
      {showSalesPersonModal && editingSalesPerson && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>{editingSalesPerson.id ? 'Edit Sales Person' : 'Add New Sales Person'}</h2>
            <form onSubmit={(e) => { e.preventDefault(); saveSalesPerson(); }}>
              <div className="form-group">
                <label>Name:</label>
                <input
                  type="text"
                  value={editingSalesPerson.name}
                  onChange={(e) => setEditingSalesPerson({ ...editingSalesPerson, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Department:</label>
                <select
                  value={editingSalesPerson.department}
                  onChange={(e) => setEditingSalesPerson({ ...editingSalesPerson, department: e.target.value })}
                  required
                >
                  <option value="security">Security Department</option>
                  <option value="printing">Printing Department</option>
                </select>
              </div>
              <div className="form-group">
                <label>Active:</label>
                <input
                  type="checkbox"
                  checked={editingSalesPerson.active}
                  onChange={(e) => setEditingSalesPerson({ ...editingSalesPerson, active: e.target.checked })}
                />
              </div>
              <div className="button-group">
                <button type="submit" className="btn-save">Save Sales Person</button>
                <button type="button" onClick={cancelSalesPersonEdit} className="btn-cancel">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommissionCalculator;
